"use client";
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FeedbackStatusConfirmDialog,
  type FeedbackStatusAction,
} from "@/components/ai/feedback/feedback-status-confirm-dialog";
import {
  Loader2,
  MessageSquare,
  RefreshCw,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  PlayCircle,
  CheckCheck,
  Clock,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import type { FeedbackStatus } from "./types";
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




// Data structure from /api/agents/email/auto-reply/feedback
// Combined ai_feedback + email_auto_replies data
type FeedbackItem = {
  // Feedback data
  feedback_id: string;
  feedback_text: string | null;
  feedback_status: FeedbackStatus;
  feedback_created_at: string | null;
  feedback_created_by: string | null;
  feedback_resolved_at: string | null;
  feedback_resolved_by: string | null;
  // Auto-reply data
  auto_reply_id: string;
  reply_text: string | null;
  sent_at: string | null;
  dry_run: boolean;
  draft_created: boolean;
  escalated: boolean;
  classification_label: string | null;
  auto_reply_created_at: string | null;
  // Email data
  email_subject: string | null;
  email_from: string | null;
};

interface AutoReplyFeedbackTableProps {
  onViewDetails?: (autoReplyId: string) => void;
  agentId?: string;
}

export function AutoReplyFeedbackTable({ onViewDetails, agentId }: AutoReplyFeedbackTableProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()


  const tUi = useTranslations("Email.AgentsEmailAutoReplyFeedbackTable")

  const { fetchWithAuth } = useAuth();
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all_with_feedback");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    feedbackId: string;
    newStatus: FeedbackStatusAction;
    feedbackText?: string;
  } | null>(null);
  const pageSize = 20;

  const loadItems = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
      });

      if (statusFilter !== "all") {
        params.set("feedback_status", statusFilter);
      }

      // Filter by agent if agentId is provided
      if (agentId) {
        params.set("agent_id", agentId);
      }

      const res = await fetchWithAuth(`/api/agents/email/auto-reply/feedback?${params}`);
      if (!res.ok) throw new Error("Failed to load feedback");

      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error(tUi("failedToLoadFeedbackList"));
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [page, statusFilter, agentId, fetchWithAuth, tUi]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleUpdateStatus = async (feedbackId: string, newStatus: 'in_progress' | 'resolved') => {
    try {
      setUpdatingId(feedbackId);
      const res = await fetchWithAuth(`/api/ai/feedback/${feedbackId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) throw new Error("Failed to update status");

      toast.success(newStatus === 'resolved' ? "Marked as resolved" : "Marked as in progress");

      // Update local state
      setItems(prev => prev.map(item =>
        item.feedback_id === feedbackId
          ? { ...item, feedback_status: newStatus }
          : item
      ));
    } catch {
      toast.error(tUi("failedToUpdateStatus"));
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString(uiFormattingLocale, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: FeedbackStatus) => {
    switch (status) {
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            <Clock className="w-3 h-3 mr-1" />
            <span><span key={tUi("pending")}>{tUi("pending")}</span></span>
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            <PlayCircle className="w-3 h-3 mr-1" />
            <span><span key={tUi("inProgress")}>{tUi("inProgress")}</span></span>
          </Badge>
        );
      case 'resolved':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCheck className="w-3 h-3 mr-1" />
            <span><span key={tUi("resolved")}>{tUi("resolved")}</span></span>
          </Badge>
        );
      default:
        return null;
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      {/* Header with filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-orange-600" />
          <h3 className="font-medium"><span key={tUi("qAFeedback")}>{tUi("qAFeedback")}</span></h3>
          <Badge variant="secondary">{total}</Badge>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="min-w-0 flex-1 sm:w-[180px]">
              <SelectValue placeholder={tUi("filterByStatus")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_with_feedback"><span key={tUi("allWithFeedback")}>{tUi("allWithFeedback")}</span></SelectItem>
              <SelectItem value="pending"><span key={tUi("pending")}>{tUi("pending")}</span></SelectItem>
              <SelectItem value="in_progress"><span key={tUi("inProgress")}>{tUi("inProgress")}</span></SelectItem>
              <SelectItem value="resolved"><span key={tUi("resolved")}>{tUi("resolved")}</span></SelectItem>
              <SelectItem value="all"><span key={tUi("allAutoReplies")}>{tUi("allAutoReplies")}</span></SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadItems} disabled={isLoading} className="shrink-0">
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p><span key={tUi("noFeedbackFound")}>{tUi("noFeedbackFound")}</span></p>
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]"><span key={tUi("status")}>{tUi("status")}</span></TableHead>
                  <TableHead><span key={tUi("email")}>{tUi("email")}</span></TableHead>
                  <TableHead className="max-w-[300px]"><span key={tUi("feedback")}>{tUi("feedback")}</span></TableHead>
                  <TableHead className="w-[120px]"><span key={tUi("created")}>{tUi("created")}</span></TableHead>
                  <TableHead className="w-[150px]"><span key={tUi("actions")}>{tUi("actions")}</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.feedback_id}>
                    <TableCell>{getStatusBadge(item.feedback_status)}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm font-medium truncate max-w-[250px]">
                          {item.email_subject || tUi("noSubject")}
                        </p>
                        <p className="text-xs text-gray-500 truncate max-w-[250px]">
                          {item.email_from}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {item.feedback_text || "-"}
                      </p>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {formatDate(item.feedback_created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {item.feedback_status === 'pending' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => setConfirmDialog({
                              feedbackId: item.feedback_id,
                              newStatus: "in_progress",
                              feedbackText: item.feedback_text || undefined,
                            })}
                            disabled={updatingId === item.feedback_id}
                            title={tUi("takeInProgress")}
                          >
                            {updatingId === item.feedback_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <PlayCircle className="w-3 h-3" />
                            )}
                          </Button>
                        )}
                        {item.feedback_status && item.feedback_status !== 'resolved' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-green-600 hover:text-green-700"
                            onClick={() => setConfirmDialog({
                              feedbackId: item.feedback_id,
                              newStatus: "resolved",
                              feedbackText: item.feedback_text || undefined,
                            })}
                            disabled={updatingId === item.feedback_id}
                            title={tUi("markAsResolved")}
                          >
                            {updatingId === item.feedback_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCheck className="w-3 h-3" />
                            )}
                          </Button>
                        )}
                        {onViewDetails && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => onViewDetails(item.auto_reply_id)}
                          >
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                <span><span key={tUi("showing")}>{tUi("showing")}</span></span> {(page - 1) * pageSize + 1} <span>-</span> {Math.min(page * pageSize, total)} <span><span key={tUi("of")}>{tUi("of")}</span></span> {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm"><TranslationText>{tUi.rich("pageValue0OfValue1", { value0: String(page), value1: String(totalPages) })}</TranslationText></span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <FeedbackStatusConfirmDialog
        open={!!confirmDialog}
        onOpenChange={(open) => !open && setConfirmDialog(null)}
        action={confirmDialog?.newStatus || null}
        feedbackText={confirmDialog?.feedbackText}
        onConfirm={() => {
          if (confirmDialog) {
            handleUpdateStatus(confirmDialog.feedbackId, confirmDialog.newStatus);
            setConfirmDialog(null);
          }
        }}
      />
    </div>
  );
}
